import { Box, Paper, Typography } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ErrorIcon from "@mui/icons-material/Error";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { BRAND_COLOR, LeadStatus } from "../constants";

interface AnalyticsKpiCardsProps {
  data: LeadsManagementData;
}

export default function AnalyticsKpiCards({ data }: AnalyticsKpiCardsProps) {
  const { aggregations, totalFiltered, goToFilteredLeads } = data;

  const countFor = (status: LeadStatus) =>
    aggregations.byStatus.find((item) => item.status === status)?.count ?? 0;
  const pending = countFor("Pending");
  const reported = countFor("Success");
  const failed = countFor("Failure");
  const denominator = reported + failed;
  const successRate = denominator ? `${Math.round((reported / denominator) * 100)}%` : "—";

  const kpis = [
    {
      label: "Total leads",
      value: totalFiltered,
      caption: "across all brands",
      icon: <GroupsIcon sx={{ color: BRAND_COLOR }} />,
      onClick: () => goToFilteredLeads({ status: "All" }),
      trackerId: "934",
    },
    {
      label: "Pending",
      value: pending,
      caption: "awaiting report",
      icon: <ScheduleIcon sx={{ color: "#E65100" }} />,
      onClick: () => goToFilteredLeads({ status: "Pending" }),
      trackerId: "935",
    },
    {
      label: "Reported",
      value: reported,
      caption: `${successRate} success rate`,
      icon: <TaskAltIcon sx={{ color: "#2E7D32" }} />,
      onClick: () => goToFilteredLeads({ status: "Success" }),
      trackerId: "936",
    },
    {
      label: "Failed",
      value: failed,
      caption: "report failed",
      icon: <ErrorIcon sx={{ color: "#C62828" }} />,
      onClick: () => goToFilteredLeads({ status: "Failure" }),
      trackerId: "937",
    },
  ];

  return (
    <Box sx={{ mt: 3.5 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color: "#1a1a1a" }}>
        High Level KPI's
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 13, color: "#9e9e9e", mb: 1.75 }}>
        <AdsClickIcon sx={{ fontSize: 16, color: BRAND_COLOR }} />
        Click any KPI, bar or data point to open the matching leads.
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2.5 }}>
        {kpis.map((kpi) => (
          <Paper
            key={kpi.label}
            variant="outlined"
            onClick={kpi.onClick}
            title="View these leads"
            data-tracker={kpi.trackerId}
            sx={{ p: "20px 22px", borderRadius: 2, cursor: "pointer" }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 500, color: BRAND_COLOR }}>
                {kpi.label.toUpperCase()}
              </Typography>
              {kpi.icon}
            </Box>
            <Typography sx={{ fontSize: 34, fontWeight: 700, color: "#1a1a1a", mt: 1.25 }}>
              {kpi.value}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#9e9e9e", mt: 0.25 }}>{kpi.caption}</Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
